import React, { useEffect } from 'react';
import '../styles/pages/Players.scss';
import '../styles/pages/About.scss';
import '../styles/components/index.scss';
import { Link } from 'react-router-dom';
import InstagramIcon from '@mui/icons-material/Instagram';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import GroupsIcon from '@mui/icons-material/Groups';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FacebookIcon from '@mui/icons-material/Facebook';
import { Helmet } from 'react-helmet';

const About = () => {
  // Set page title
  useEffect(() => {
    document.title = "About FirstWhistle";
  }, []);

  return (
    <div className="about-page">
      <Helmet>
        <title>About FirstWhistle | Our Story and Mission</title>
        <meta name="description" content="Learn about FirstWhistle's mission to transform grassroots football with innovative technology. Founded by Dean Hodges, we're making football more enjoyable and connected for everyone." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://firstwhistle.app/about" />
        <meta property="og:title" content="About FirstWhistle | Our Story and Mission" />
        <meta property="og:description" content="Learn about FirstWhistle's mission to transform grassroots football with innovative technology. Founded by Dean Hodges, we're making football more enjoyable and connected for everyone." />
        <meta property="og:image" content="https://firstwhistle.app/images/about-hero-bg.jpeg" />
        <meta property="og:site_name" content="FirstWhistle" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://firstwhistle.app/about" />
        <meta name="twitter:title" content="About FirstWhistle | Our Story and Mission" />
        <meta name="twitter:description" content="Learn about FirstWhistle's mission to transform grassroots football with innovative technology. Founded by Dean Hodges, we're making football more enjoyable and connected for everyone." />
        <meta name="twitter:image" content="https://firstwhistle.app/images/about-hero-bg.jpeg" />
        
        <link rel="canonical" href="https://firstwhistle.app/about" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "AboutPage",
              "name": "About FirstWhistle",
              "description": "Learn about FirstWhistle's mission to transform grassroots football with innovative technology. Founded by Dean Hodges, we're making football more enjoyable and connected for everyone.",
              "mainEntity": {
                "@type": "Organization",
                "name": "FirstWhistle",
                "description": "FirstWhistle is an AI-powered football team management application for grassroots teams.",
                "url": "https://firstwhistle.app",
                "founder": {
                  "@type": "Person",
                  "name": "Dean Hodges",
                  "jobTitle": "Founder",
                  "description": "Parent, Volunteer Linesman, Lifelong Football Organiser"
                },
                "foundingDate": "2024",
                "slogan": "Where every player counts",
                "mission": "To make football more enjoyable, more connected, and more rewarding for everyone involved.",
                "knowsAbout": [
                  "Grassroots Football",
                  "Team Management",
                  "Youth Sports Development",
                  "Football Technology"
                ],
                "sameAs": [
                  "https://instagram.com/firstwhistleapp"
                ]
              }
            }
          `}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="about-hero-section">
        <div className="about-hero-content">
          <h1>Our Story</h1>
          <p className="tagline">Transforming grassroots football with passion and innovation</p>
        </div>
        <div className="hero-background">
          <img src={require('../images/about-hero-bg.jpeg')} alt="Football pitch" className="about-hero-image" />
          <div className="overlay"></div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="mission-section">
        <div className="mission-container">
          <div className="mission-icon">
            <SportsSoccerIcon />
          </div>
          <h2>Our Mission</h2>
          <p>To make football more enjoyable, more connected, and more rewarding for everyone involved.</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="about-content-section">
        <div className="about-container">
          <div className="about-content">
            <div className="founder-story">
              <div className="founder-image">
                {/* Use a placeholder image if founder.jpeg doesn't exist */}
                <img src={require('../images/about-the-founder.jpg')} alt="Dean Hodges - Founder" />
              </div>
              <div className="founder-details">
                <h2>Meet Our Founder</h2>
                <h3>Dean Hodges</h3>
                <p className="founder-role">Parent, Volunteer Linesman, Lifelong Football Organiser</p>
              </div>
            </div>

            <div className="brand-story">
              <p>
                At FirstWhistle, we believe grassroots football deserves the same energy and innovation as the professional game. That's why we built a platform that puts players at the centre — tracking stats, celebrating progress, and making every match feel like matchday.
              </p>
              
              <p>
                Designed with input from coaches and trusted by parents, FirstWhistle connects teams on and off the pitch, simplifying everything from availability to awards.
              </p>
              
              <div className="story-highlight">
                <div className="highlight-content">
                  <p>
                    Founded by Dean Hodges, a parent, volunteer linesman, and lifelong football organiser, FirstWhistle was born from years of sideline experience. Frustrated by juggling WhatsApp chats and disorganised teams, Dean wanted a better way — combining the fun of FIFA with the logic of Championship Manager to create something players love and parents can trust.
                  </p>
                </div>
              </div>
              
              <p>
                His mission: to make football more enjoyable, more connected, and more rewarding for everyone involved.
              </p>
            </div>
          </div>

          {/* Remove image gallery for now since we don't have the images */}
          {/* <div className="photo-gallery">
            <h2>Moments that Inspired Us</h2>
            <div className="gallery-grid">
              <div className="gallery-item">
                <img src={require('../images/players-hero-bg.jpg')} alt="Team on football pitch" />
              </div>
              <div className="gallery-item">
                <img src={require('../images/players-hero-bg.jpg')} alt="Kids playing football" />
              </div>
              <div className="gallery-item">
                <img src={require('../images/players-hero-bg.jpg')} alt="Coaches with team" />
              </div>
              <div className="gallery-item">
                <img src={require('../images/players-hero-bg.jpg')} alt="Football match" />
              </div>
            </div>
          </div> */}
        </div>
      </section>

      {/* Value Proposition */}
      <section className="values-section">
        <h2>What We Stand For</h2>
        <div className="values-container">
          <div className="value-card">
            <div className="value-icon">
              <SportsSoccerIcon />
            </div>
            <h3>Player-Focused</h3>
            <p>We put young players at the heart of everything we do, making them feel like stars.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">
              <GroupsIcon />
            </div>
            <h3>Team Spirit</h3>
            <p>We foster connection and community between players, coaches, and parents.</p>
          </div>
          <div className="value-card">
            <div className="value-icon">
              <EmojiEventsIcon />
            </div>
            <h3>Celebration</h3>
            <p>We celebrate every achievement, big or small, making grassroots football more rewarding.</p>
          </div>
        </div>
      </section>

      {/* Join Us Section */}
      <section className="join-section">
        <div className="join-container">
          <h2>Join Our Journey</h2>
          <p>Be part of the grassroots football revolution. Experience FirstWhistle for yourself.</p>
          <div className="cta-buttons">
            <Link to="/signup" className="cta-button primary">Join Now</Link>
            <a href="https://www.instagram.com/firstwhistle.app" target="_blank" rel="noopener noreferrer" className="cta-button secondary">
              <InstagramIcon /> Follow Our Story
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <p className="tagline">FirstWhistle – Where every player counts.</p>
          </div>
          <div className="footer-navigation">
            <div className="footer-links">
              <Link to="/">Home</Link>
              <Link to="/players">Players</Link>
              <Link to="/coaches">Coaches</Link>
              <Link to="/parents">Parents</Link>
              <Link to="/about">About</Link>
              <Link to="/contact">Contact</Link>
            </div>
            <div className="footer-socials">
              <a href="https://www.instagram.com/firstwhistle.app" target="_blank" rel="noopener noreferrer">
                <InstagramIcon />
              </a>
              <a href="https://www.facebook.com/FirstWhistleApp" target="_blank" rel="noopener noreferrer">
                <FacebookIcon />
              </a>
            </div>
          </div>
          <div className="footer-legal">
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About; 